// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

/**
 * Room spectator list management.
 *
 * Tracks which spectators are watching the current room and whether
 * spectating is allowed. This is the host-side view of spectators,
 * distinct from spectatorStore.ts which manages the experience of
 * being a spectator yourself.
 */

import { create } from 'zustand';

import { logger } from '../lib/logger';
import type { SpectatorInfo } from '../types/game';

export interface RoomSpectatorStore {
  // State
  spectators: SpectatorInfo[];
  allowSpectators: boolean;

  // Actions
  addSpectator: (spectator: SpectatorInfo) => void;
  removeSpectator: (playerId: string) => void;
  setSpectators: (spectators: SpectatorInfo[]) => void;
  setAllowSpectators: (allow: boolean) => void;
  reset: () => void;
}

const initialState = {
  spectators: [] as SpectatorInfo[],
  allowSpectators: true,
};

export const useRoomSpectatorStore = create<RoomSpectatorStore>()((set, get) => ({
  ...initialState,

  addSpectator: (spectator: SpectatorInfo) => {
    set(current => ({
      spectators: [...current.spectators, spectator],
    }));
    logger.info('spectator_added', { spectator });
  },

  removeSpectator: (playerId: string) => {
    const { spectators } = get();
    logger.info('spectator_removed', { playerId });
    set({ spectators: spectators.filter(s => s.player_id !== playerId) });
  },

  setSpectators: (spectators: SpectatorInfo[]) => {
    logger.info('spectators_set', { count: spectators.length });
    set({ spectators });
  },

  setAllowSpectators: (allow: boolean) => {
    logger.info('allow_spectators_set', { allow });
    set({ allowSpectators: allow });
  },

  reset: () => set(initialState),
}));
